import type { GroupColorSlot } from "./workspace.types";

/**
 * Radix Themes color-scale identifier used for the `color` prop on Radix
 * components (Badge, IconButton, Menu.Item, etc.) and as the scale prefix
 * for the raw CSS custom property (`--{color}-9`).
 *
 * Layout Labs uses eight of Radix's palette scales — recycled across A–Z
 * groups. The identifier is also the palette name Radix exposes via
 * `theme.css`, so every consumer can go straight from a `GroupColorSlot` to a
 * Radix component `color` prop or an inline paint fill without a bridging
 * layer.
 */
export type RadixGroupColor =
  | "blue"
  | "green"
  | "yellow"
  | "orange"
  | "red"
  | "purple"
  | "pink"
  | "teal";

/**
 * Identity map: `GroupColorSlot` → Radix Themes color scale name.
 *
 * Every slot maps to the like-named Radix scale — the tuple is intentionally
 * verbose so callers can `import { GROUP_COLOR_TO_RADIX }` and get the whole
 * table (and TypeScript exhaustiveness) rather than reaching for a `string`
 * cast every time.
 */
export const GROUP_COLOR_TO_RADIX: Record<GroupColorSlot, RadixGroupColor> = {
  blue: "blue",
  green: "green",
  yellow: "yellow",
  orange: "orange",
  red: "red",
  purple: "purple",
  pink: "pink",
  teal: "teal",
} as const;

/**
 * Returns the CSS custom-property reference for a group's accent paint step
 * (Radix step 9 — the solid step used for indicators, tracks, and accent
 * fills). Used where a Radix component `color` prop cannot apply — for
 * example, the color dot inside the `GroupSelect` trigger. Resolves through
 * Radix Themes' own light/dark scales, so no manual appearance branch is
 * required.
 */
export const groupAccentCssVar = (slot: GroupColorSlot): string =>
  `var(--${GROUP_COLOR_TO_RADIX[slot]}-9)`;
